// regionCheck.ts
// ===============
//
// Wave 14ef: Geteilter Helper fuer "ist diese Lat/Lon in einer unserer
// unterstuetzten Regionen?" Aufrufer:
//   - OutOfRegionBanner (App-weiter GPS-Hinweis)
//   - RouterPage (Pin setzen, GPS-Start, Koordinaten einfuegen)
//   - CreateCustomPage.handleUseMyPosition
//
// Wave 14ec (2026-08-04) — DER BUG, DER KORRIDOR-NAVIGATION UNMOEGLICH MACHTE:
//
// Diese Datei kannte bisher NUR die statisch einkompilierten REGIONS aus
// @adtu/shared — und da steht genau eine Region drin: '3pbr'. Ein User, der
// sich einen eigenen Korridor gebaut hat ("Andorra meer #2", "Ventimiglia
// La Spezia") und darin routen wollte, lief in folgende Wand:
//
//   RouterPage.tsx:449  Tap auf die Karte      -> "ausserhalb der Karte"
//   RouterPage.tsx:501  GPS "Meine Position"   -> "ausserhalb der Karte"
//   RouterPage.tsx:706  Start einfuegen        -> "ausserhalb der Karte"
//   RouterPage.tsx:724  Ziel einfuegen         -> "ausserhalb der Karte"
//
// Der Client hat also blockiert, BEVOR ueberhaupt ein Request rausging.
// Das Backend konnte die Route seit Wave 14dz laengst berechnen — es wurde
// nur nie gefragt. Dazu kam der OutOfRegionBanner, der dem User dauerhaft
// "du bist ausserhalb der unterstuetzten Region" anzeigte, waehrend er
// mitten in seinem eigenen, fertig gebauten Korridor stand.
//
// Fix: die Bounds der dynamischen Korridor-Regionen kommen aus dem
// Tiles-Manifest (backend/routes/tiles.js -> corridorRegionEntry(), bounds
// = bbox aus build-meta.json) und werden hier registriert. api.tilesManifest()
// ruft setDynamicRegions() bei JEDEM Manifest-Load auf (Netz ODER IDB-Cache).
//
// Persistenz: die Bounds landen zusaetzlich im localStorage. Damit ist der
// Check schon beim allerersten Frame korrekt — auch offline, auch bevor das
// Manifest geladen ist. Genau das ist der Fall, der auf dem Rad zaehlt:
// kein Netz, App frisch gestartet, Route im eigenen Korridor planen.

import { REGIONS, type RegionInfo } from '@adtu/shared';

type Bounds = RegionInfo['bounds'];

export interface DynamicRegion {
  id: string;
  humanName: string;
  bounds: Bounds;
}

const LS_KEY = 'adtu-dynamic-region-bounds-v1';

function readPersisted(): DynamicRegion[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isUsable);
  } catch {
    return [];
  }
}

function isUsable(r: unknown): r is DynamicRegion {
  if (!r || typeof r !== 'object') return false;
  const b = (r as DynamicRegion).bounds as Bounds | null | undefined;
  if (!b) return false;
  return (
    Number.isFinite(b.north) &&
    Number.isFinite(b.south) &&
    Number.isFinite(b.east) &&
    Number.isFinite(b.west)
  );
}

let dynamicRegions: DynamicRegion[] = readPersisted();

/**
 * Registriert die Regionen aus dem Tiles-Manifest (inkl. selbst gebauter
 * Korridore) fuer die Punkt-in-Region-Pruefung. Idempotent, billig, und
 * bewusst tolerant: Eintraege ohne bounds (Build-Meta fehlt) werden still
 * ignoriert statt die Liste zu kippen.
 */
export function setDynamicRegions(
  list: Array<{ id: string; humanName: string; bounds?: Bounds | null }>,
): void {
  const next = list.filter(isUsable).map((r) => ({
    id: r.id,
    humanName: r.humanName,
    bounds: r.bounds as Bounds,
  }));
  // Leere Liste NICHT persistieren: ein kurzzeitig leeres Manifest (Server
  // gerade neu gestartet, Auth abgelaufen) wuerde dem User sonst offline
  // seinen Korridor unter den Fuessen wegziehen.
  if (next.length === 0) return;
  dynamicRegions = next;
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(next));
  } catch {
    /* Speicher voll / Private Mode — in-memory reicht fuer diese Session */
  }
}

/**
 * Mittelpunkt einer dynamischen Region (Bbox-Mitte). Fuer die Start-Kamera:
 * ohne das oeffnet die Router-Karte bei einem Andorra-Korridor ueber
 * Italien (CORRIDOR_CENTER), weil REGIONS den Slug nicht kennt.
 */
export function dynamicRegionCenter(id: string): [number, number] | null {
  const r = dynamicRegions.find((x) => x.id === id);
  if (!r) return null;
  return [(r.bounds.west + r.bounds.east) / 2, (r.bounds.south + r.bounds.north) / 2];
}

/** Klarname einer dynamischen Region ("Andorra meer #2") oder null. */
export function dynamicRegionName(id: string): string | null {
  return dynamicRegions.find((x) => x.id === id)?.humanName ?? null;
}

/** Aktuell bekannte dynamische Regionen (fuer Debug/Anzeige). */
export function getDynamicRegions(): DynamicRegion[] {
  return dynamicRegions;
}

function isInsideBounds(lat: number, lon: number, b: Bounds): boolean {
  return lat >= b.south && lat <= b.north && lon >= b.west && lon <= b.east;
}

/**
 * True wenn (lat, lon) in mindestens einer bekannten Region liegt —
 * statisch kompiliert ODER selbst gebauter Korridor.
 *
 * Bewusst grob: die Korridor-Bbox ist groesser als der eigentliche
 * Korridor-Streifen. Das ist Absicht — dieser Check ist nur ein
 * Vorfilter gegen offensichtlichen Unsinn (Pin in Kanada). Ob Start und
 * Ziel wirklich im Korridor liegen, entscheidet das Backend praezise
 * (`out_of_corridor` / `cross_segment`), und RouterPage uebersetzt diese
 * Fehler bereits in Klartext. Lieber einmal zu viel fragen als den User
 * in seinem eigenen Korridor aussperren.
 */
export function hasAnyRegion(): boolean {
  return Object.keys(REGIONS).length > 0 || dynamicRegions.length > 0;
}

export function isInsideAnyRegion(lat: number, lon: number): boolean {
  // Wave 14ed: FAIL-OPEN. Seit die statische Registry leer ist (kein '3pbr'
  // mehr einkompiliert), hat ein frischer Account NULL bekannte Regionen.
  // Wuerde diese Funktion dann false liefern, waere die halbe App
  // clientseitig tot: RouterPage/CreateCustomPage blocken jeden Tap, bevor
  // ein Request rausgeht, und der User saehe nur "ausserhalb der Region",
  // ohne zu verstehen warum. Ohne bekannte Region gibt es auch nichts,
  // wogegen man pruefen koennte — also durchlassen und den Server
  // antworten lassen (der hat Klartext-Fehler wie navi_unavailable).
  if (!hasAnyRegion()) return true;
  for (const region of Object.values(REGIONS)) {
    if (isInsideBounds(lat, lon, region.bounds)) return true;
  }
  for (const region of dynamicRegions) {
    if (isInsideBounds(lat, lon, region.bounds)) return true;
  }
  return false;
}

/**
 * Klartext-Liste der Regionen — "3 Peaks Route, Andorra meer #2 or
 * Ventimiglia La Spezia". Enthaelt die eigenen Korridore, damit der
 * Hinweistext nicht Regionen verschweigt, die der User selbst gebaut hat.
 */
export function regionNamesList(): string {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const r of Object.values(REGIONS)) {
    if (!seen.has(r.humanName)) {
      seen.add(r.humanName);
      names.push(r.humanName);
    }
  }
  for (const r of dynamicRegions) {
    if (!seen.has(r.humanName)) {
      seen.add(r.humanName);
      names.push(r.humanName);
    }
  }
  if (names.length <= 1) return names[0] ?? '';
  if (names.length === 2) return `${names[0]} or ${names[1]}`;
  const head = names.slice(0, -1).join(', ');
  const tail = names[names.length - 1];
  return `${head} or ${tail}`;
}
